import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './KanjiLookup.css';
import kanjiData from '../Languages/Japanese/kanjiDefinitions.json';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import logger from '../utils/logger';
import { useWordContext } from '../context/WordContext';
import { ReactComponent as SpeakerIcon } from '../images/icons/speaker.svg';

const KanjiLookup = ({ word, children, sentence }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    const [data, setData] = useState(null);
    const [contextData, setContextData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openAccordionIndex, setOpenAccordionIndex] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null); // All accordions closed by default
    const [wordGrade, setWordGrade] = useState(1);
    const [wordKnown, setWordKnown] = useState(false);
    const [kanjiGrades, setKanjiGrades] = useState({});
    const [kanjiKnownStatus, setKanjiKnownStatus] = useState({});
    // Add a local state to track if word was just saved to force UI update
    const [justSavedWord, setJustSavedWord] = useState(false);
    const [justSavedKanji, setJustSavedKanji] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const cachedAudioUrl = useRef(null);

    // Log sentence parameter for debugging
    logger.debug(`KanjiLookup initialized with word: "${word}", sentence: "${sentence || 'none'}"`);

    // Use the context instead of state and direct API calls
    const {
        isWordSaved,
        isKanjiSaved,
        addSavedWord,
        removeSavedWord,
        addSavedKanji,
        removeSavedKanji,
        getWordGrade,
        isWordKnown,
        getKanjiGrade,
        isKanjiKnown,
        updateWordGrade,
        updateWordKnown,
        updateKanjiGrade,
        updateKanjiKnown,
        refreshSavedWords
    } = useWordContext();

    // Get saved status from context
    const isSaved = isWordSaved(word, 'Japanese') || justSavedWord;
    logger.debug(`KanjiLookup - Word "${word}" saved status:`, isSaved);

    // Get saved kanji list
    const savedKanjiList = Array.from(word)
        .filter(char => /[\u4E00-\u9FFF]/.test(char))
        .filter(char => isKanjiSaved(char) || justSavedKanji === char);
    logger.debug(`KanjiLookup - Saved kanji in word:`, savedKanjiList);

    // Initialize kanji grades and known status
    useEffect(() => {
        const newGrades = {};
        const newKnownStatus = {};

        Array.from(word)
            .filter(char => /[\u4E00-\u9FFF]/.test(char))
            .forEach(char => {
                if (isKanjiSaved(char)) {
                    newGrades[char] = getKanjiGrade(char);
                    newKnownStatus[char] = isKanjiKnown(char);
                } else {
                    newGrades[char] = 1;
                    newKnownStatus[char] = false;
                }
            });

        setKanjiGrades(newGrades);
        setKanjiKnownStatus(newKnownStatus);
        logger.debug('KanjiLookup - Updated kanji grades:', newGrades);
    }, [word, isKanjiSaved, getKanjiGrade, isKanjiKnown, justSavedKanji]);

    // Update local state when the word's saved status changes
    useEffect(() => {
        if (isSaved) {
            setWordGrade(getWordGrade(word, 'Japanese'));
            setWordKnown(isWordKnown(word, 'Japanese'));
            logger.debug(`KanjiLookup - Word "${word}" is saved with grade:`, getWordGrade(word, 'Japanese'));
        } else {
            setWordGrade(1);
            setWordKnown(false);
        }
    }, [isSaved, word, getWordGrade, isWordKnown]);

    // Reset state to avoid stale popups when the word or sentence changes
    useEffect(() => {
        setData(null);
        setContextData(null);
        setShowPopup(false);
        setError(null);
        setOpenAccordion(null);
        setOpenAccordionIndex(null);
    }, [word, sentence]);

    const spanRef = useRef(null);
    const popupRef = useRef(null);

    // Helper function to get userId from multiple sources
    const getUserId = () => {
        // Always try to get the latest userId from the JWT token first
        const token = localStorage.getItem('access_token');
        if (token) {
            try {
                // Decode JWT token to get user info
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));

                const decodedToken = JSON.parse(jsonPayload);
                if (decodedToken.sub) {
                    const userId = decodedToken.sub;
                    logger.debug(`KanjiLookup - Extracted user ID from token: ${userId}`);
                    // Update localStorage with the current userId from the token
                    localStorage.setItem('userId', userId);
                    return userId;
                }
            } catch (err) {
                logger.error("KanjiLookup - Error decoding token:", err);
            }
        }

        // Fall back to localStorage only if token extraction fails
        const localUserId = localStorage.getItem('userId');
        if (localUserId) {
            logger.debug("KanjiLookup - Using userId from localStorage (fallback)");
            return localUserId;
        }

        logger.warn("KanjiLookup - No userId available from token or localStorage");
        return null;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            logger.debug(`KanjiLookup - Fetching data for word: "${word}", sentence context: "${sentence || 'none'}"`);

            // Use our backend proxy for Japanese lookups to avoid CORS issues
            const jishoResponse = await apiClient.get(
                `/api/jisho?keyword=${encodeURIComponent(word)}`
            );

            if (!jishoResponse.ok) {
                throw new Error(`Jisho API returned status ${jishoResponse.status}`);
            }

            const jishoData = await jishoResponse.json();
            setData(jishoData);
            logger.debug("Jisho data:", jishoData);

            // If we have a sentence context, fetch contextual information
            if (sentence) {
                logger.debug(`KanjiLookup - Have sentence context: "${sentence}"`);

                // IMPORTANT FIX: We must pass the FULL word to the context generator 
                // so the AI generates examples for the whole word, not just its first kanji!
                const targetTerm = word;

                logger.debug(`KanjiLookup - Will use "${targetTerm}" for context lookup`);
                logger.info(`KanjiLookup - Fetching context info for "${targetTerm}" in sentence "${sentence}"`);

                try {
                    const contextResponse = await apiClient.get(
                        `/api/kanjiContext?kanji=${encodeURIComponent(targetTerm)}&sentence=${encodeURIComponent(sentence)}`
                    );

                    if (!contextResponse.ok) {
                        logger.error(`KanjiLookup - Context API error status ${contextResponse.status} for "${targetTerm}"`);
                        // Create fallback context instead of throwing error
                        setContextData({
                            context_note: `The word "${targetTerm}" appears in the sentence. (Server error - context analysis unavailable)`,
                            grammar_note: "Grammar information unavailable at this time."
                        });
                    } else {
                        // Parse response
                        let contextResult;
                        try {
                            contextResult = await contextResponse.json();
                        } catch (jsonError) {
                            logger.error("Error parsing context response JSON:", jsonError);
                            // Set fallback if JSON parsing fails
                            contextResult = {
                                context_note: `The word "${word}" appears in the context of "${sentence}". (JSON parsing error)`,
                                grammar_note: "Grammar information unavailable due to parsing error."
                            };
                        }

                        logger.info(`KanjiLookup - Received context data for "${targetTerm}":`, contextResult);
                        setContextData(contextResult);
                    }
                } catch (contextError) {
                    logger.error(`KanjiLookup - Error fetching context:`, contextError);
                    // Create fallback context data if API call fails
                    setContextData({
                        context_note: `The word "${word}" appears in this sentence. (Network error - using offline mode)`,
                        grammar_note: "Grammar information unavailable in offline mode."
                    });
                }
            } else {
                logger.debug("KanjiLookup - No sentence context available");
                setContextData(null);
            }
        } catch (err) {
            logger.error("KanjiLookup - Error fetching definition:", err);
            setError(err.message || "Failed to fetch definition");

            // If we have a sentence but failed to get context, create a fallback context
            if (sentence) {
                logger.debug("Creating fallback context data");
                setContextData({
                    context_note: `The word "${word}" appears in the context of "${sentence}". This feature requires a running server for complete context information.`,
                    grammar_note: "Grammar information requires the server API to be running properly."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClick = () => {
        const newShowPopup = !showPopup;
        setShowPopup(newShowPopup);

        // Calculate popup position identically to WordLookup
        if (spanRef.current) {
            const rect = spanRef.current.getBoundingClientRect();
            setPopupPosition({
                top: rect.top + window.scrollY - 50, // Position above the word
                left: rect.left + window.scrollX,
            });
        }

        if (newShowPopup && !data && !loading && !error) {
            fetchData();
        } else if (newShowPopup && !contextData && sentence && !loading) {
            // Try to fetch context data again if we have a sentence but no context data
            fetchData();
        }
    };

    // Handle accordion toggling - only one section can be open at a time
    const toggleAccordion = (section, e) => {
        e.stopPropagation();
        setOpenAccordion(openAccordion === section ? null : section);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                showPopup &&
                spanRef.current &&
                popupRef.current &&
                !spanRef.current.contains(e.target) &&
                !popupRef.current.contains(e.target)
            ) {
                setShowPopup(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPopup]);

    const handleSaveWord = async (e) => {
        e.stopPropagation();
        const userId = getUserId();

        if (!userId) {
            logger.error("KanjiLookup - No user ID available, cannot save word");
            setError("No user ID available. Please log in again.");
            return;
        }

        // Extract definition text from Jisho data
        let definitionText = '';
        if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
            const firstResult = data.data[0];
            if (firstResult && firstResult.senses && firstResult.senses.length > 0) {
                definitionText = firstResult.senses
                    .map(sense => sense.english_definitions?.join(', ') || '')
                    .filter(Boolean)
                    .join('; ');
            }
        }

        // Extract example sentences from context data
        let exampleSentences = [];
        if (contextData && Array.isArray(contextData.examples) && contextData.examples.length > 0) {
            exampleSentences = contextData.examples;
        }

        try {
            logger.debug(`KanjiLookup - Saving word "${word}" with userId ${userId}`);
            const result = await apiClient.post('/api/saveUserWord', {
                userId,
                language: 'Japanese',
                item: word,
                grade: 1,
                known: false,
                definition: definitionText,
                exampleSentences: exampleSentences
            });

            if (!result || !result.message) {
                logger.error("KanjiLookup - Failed to save word: Invalid response");
                throw new Error("Failed to save word: Invalid response");
            }

            // Update context with language parameter and definition/examples
            addSavedWord(word, 'Japanese', {
                grade: 1,
                known: false,
                definition: definitionText,
                exampleSentences: exampleSentences
            });

            // Set local state to force UI update immediately
            setJustSavedWord(true);

            // Force refresh to update all saved words
            refreshSavedWords();

            // Set default grade and known state
            setWordGrade(1);
            setWordKnown(false);
        } catch (error) {
            logger.error("KanjiLookup - Error saving word:", error);
            setError("Failed to save word. Please try again.");
        }
    };

    const handleDeleteWord = async (e) => {
        e.stopPropagation();
        const userId = getUserId();

        if (!userId) {
            logger.error("KanjiLookup - No user ID available, cannot delete word");
            setError("No user ID available. Please log in again.");
            return;
        }

        try {
            const result = await apiClient.post('/api/removeUserWord', {
                userId,
                language: 'Japanese',
                item: word
            });

            if (!result || !result.message) {
                throw new Error("Failed to delete word: Invalid response");
            }

            // Update context
            removeSavedWord(word, 'Japanese');

            // Reset saved status
            setJustSavedWord(false);

            // Force refresh
            refreshSavedWords();
        } catch (error) {
            logger.error("KanjiLookup - Error deleting word:", error);
            setError("Failed to delete word. Please try again.");
        }
    };

    const handleGradeChange = async (e) => {
        e.stopPropagation();
        const newGrade = parseInt(e.target.value, 10);
        setWordGrade(newGrade);

        if (isSaved) {
            try {
                const success = await updateWordGrade(word, 'Japanese', newGrade);
                if (!success) {
                    setError("Failed to update grade. Please try again.");
                }
            } catch (error) {
                setError("Failed to update grade. Please try again.");
            }
        }
    };

    const handleKnownChange = async (e) => {
        e.stopPropagation();
        const isKnown = e.target.checked;
        setWordKnown(isKnown);

        if (isSaved) {
            try {
                const success = await updateWordKnown(word, 'Japanese', isKnown);
                if (!success) {
                    setError("Failed to update status. Please try again.");
                }
            } catch (error) {
                setError("Failed to update status. Please try again.");
            }
        }
    };

    const handleSaveKanji = async (kanjiChar) => {
        const userId = getUserId();

        if (!userId) {
            setError("No user ID available. Please log in again.");
            return;
        }

        try {
            const result = await apiClient.post('/api/saveUserKanji', {
                userId,
                kanji: kanjiChar,
                grade: 1,
                known: false,
                type: 'kanji'
            });

            if (!result || !result.message) {
                throw new Error("Failed to save kanji: Invalid response");
            }

            // Update context
            addSavedKanji(kanjiChar);

            // Set local state to force immediate UI update
            setJustSavedKanji(kanjiChar);

            // Force refresh
            refreshSavedWords();

            // Update grades and known status
            setKanjiGrades(prev => ({ ...prev, [kanjiChar]: 1 }));
            setKanjiKnownStatus(prev => ({ ...prev, [kanjiChar]: false }));
        } catch (error) {
            setError("Failed to save kanji. Please try again.");
        }
    };

    const handleDeleteKanji = async (kanjiChar) => {
        const userId = getUserId();

        if (!userId) {
            setError("No user ID available. Please log in again.");
            return;
        }

        try {
            const result = await apiClient.post('/api/deleteUserKanji', {
                userId,
                kanji: kanjiChar
            });

            if (!result || !result.message) {
                throw new Error("Failed to delete kanji: Invalid response");
            }

            // Update context
            removeSavedKanji(kanjiChar);

            // Reset saved status for this kanji
            setJustSavedKanji(prev => prev === kanjiChar ? null : prev);

            // Force refresh
            refreshSavedWords();
        } catch (error) {
            setError("Failed to delete kanji. Please try again.");
        }
    };

    const handleKanjiGradeChange = async (kanjiChar, e) => {
        e.stopPropagation();
        const newGrade = parseInt(e.target.value, 10);

        // Update local state immediately for better UX
        setKanjiGrades(prev => ({
            ...prev,
            [kanjiChar]: newGrade
        }));

        if (isKanjiSaved(kanjiChar)) {
            try {
                const success = await updateKanjiGrade(kanjiChar, newGrade);
                if (!success) {
                    setError("Failed to update grade. Please try again.");
                }
            } catch (error) {
                setError("Failed to update grade. Please try again.");
            }
        }
    };

    const handleKanjiKnownChange = async (kanjiChar, e) => {
        e.stopPropagation();
        const isKnown = e.target.checked;

        // Update local state immediately for better UX
        setKanjiKnownStatus(prev => ({
            ...prev,
            [kanjiChar]: isKnown
        }));

        if (isKanjiSaved(kanjiChar)) {
            try {
                const success = await updateKanjiKnown(kanjiChar, isKnown);
                if (!success) {
                    setError("Failed to update status. Please try again.");
                }
            } catch (error) {
                setError("Failed to update status. Please try again.");
            }
        }
    };

    const kanjiChars = Array.from(word).filter((char) => /[\u4E00-\u9FFF]/.test(char));
    const kanjiDetails = kanjiChars.map((char) => {
        const definition = kanjiData.find((item) => item.literal.trim() === char.trim());
        return { literal: char, definition };
    });

    let primaryDefinition = "No definition found.";
    if (data) {
        if (data.definitions && Array.isArray(data.definitions) && data.definitions.length > 0) {
            primaryDefinition = data.definitions[0];
        } else if (data.data && Array.isArray(data.data) && data.data.length > 0) {
            const entry = data.data[0];
            const jp = entry.japanese && entry.japanese[0];
            const sense = entry.senses && entry.senses[0];
            primaryDefinition = jp
                ? `${jp.word || ''} (${jp.reading || ''}): ${sense ? sense.english_definitions.join(', ') : ''}`
                : '';
        }
        primaryDefinition = primaryDefinition
            ? primaryDefinition.replace(new RegExp(`^${word}\\s*[:\\-]?\\s*`), '')
            : primaryDefinition;
    }

    // Render word grading UI - only show if word is saved
    const renderWordGradingUI = () => {
        if (!isSaved) return null;

        return (
            <div className="stories-kanji-grading" onClick={(e) => e.stopPropagation()}>
                <div className="difficulty-scale-stories" onClick={(e) => e.stopPropagation()}>
                    <span className="stories-difficulty-hard">Hard</span>
                    <span className="stories-difficulty-easy">Easy</span>
                </div>
                <div className="stories-radio-group">
                    {[1, 2, 3, 4, 5].map(grade => (
                        <div
                            key={grade}
                            className={`stories-radio-option stories-radio-grade-${grade} ${wordGrade === grade ? 'selected' : ''}`}
                        >
                            <input
                                type="radio"
                                id={`grade-word-${grade}`}
                                name="word-grade"
                                value={grade}
                                checked={wordGrade === grade}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleGradeChange(e);
                                }}
                            />
                            <label htmlFor={`grade-word-${grade}`} onClick={(e) => e.stopPropagation()}>{grade}</label>
                        </div>
                    ))}
                </div>

                <div className="stories-known-checkbox">
                    <input
                        type="checkbox"
                        id="word-known"
                        checked={wordKnown}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleKnownChange(e);
                        }}
                    />
                    <div>
                        <label htmlFor="word-known" className="stories-known-checkbox-label" onClick={(e) => e.stopPropagation()}>
                            I know this word now.
                        </label>
                        <div className="stories-known-checkbox-description" onClick={(e) => e.stopPropagation()}>
                            Check this box to remove the highlight.
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render grading UI for a kanji character - only show if kanji is saved
    const renderKanjiGradingUI = (kanjiChar) => {
        if (!isKanjiSaved(kanjiChar) && justSavedKanji !== kanjiChar) return null;

        return (
            <div className="stories-kanji-grading" onClick={(e) => e.stopPropagation()}>
                <div className="difficulty-scale-stories" onClick={(e) => e.stopPropagation()}>
                    <span className="stories-difficulty-hard">Hard</span>
                    <span className="stories-difficulty-easy">Easy</span>
                </div>
                <div className="stories-radio-group">
                    {[1, 2, 3, 4, 5].map(grade => (
                        <div
                            key={grade}
                            className={`stories-radio-option stories-radio-grade-${grade} ${kanjiGrades[kanjiChar] === grade ? 'selected' : ''}`}
                        >
                            <input
                                type="radio"
                                id={`grade-${kanjiChar}-${grade}`}
                                name={`kanji-grade-${kanjiChar}`}
                                value={grade}
                                checked={kanjiGrades[kanjiChar] === grade}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleKanjiGradeChange(kanjiChar, e);
                                }}
                            />
                            <label htmlFor={`grade-${kanjiChar}-${grade}`} onClick={(e) => e.stopPropagation()}>{grade}</label>
                        </div>
                    ))}
                </div>

                <div className="stories-known-checkbox">
                    <input
                        type="checkbox"
                        id={`known-${kanjiChar}`}
                        checked={kanjiKnownStatus[kanjiChar] || false}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleKanjiKnownChange(kanjiChar, e);
                        }}
                    />
                    <div>
                        <label htmlFor={`known-${kanjiChar}`} className="stories-known-checkbox-label" onClick={(e) => e.stopPropagation()}>
                            I know this kanji now.
                        </label>
                        <div className="stories-known-checkbox-description" onClick={(e) => e.stopPropagation()}>
                            Check this box to remove the highlight.
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Get class for kanji character based on saved status and grade
    const getKanjiClass = (kanjiChar) => {
        const isKanjiActuallySaved = isKanjiSaved(kanjiChar) || justSavedKanji === kanjiChar;
        if (!isKanjiActuallySaved) return '';

        let className = 'saved-word';

        // Add grade-specific class
        className += ` grade-${kanjiGrades[kanjiChar] || 1}`;

        // If kanji is marked as known, don't apply saved-word styling
        if (kanjiKnownStatus[kanjiChar]) {
            className = '';
        }

        return className;
    };

    // Get class for the word based on saved status and grade
    const getWordClass = () => {
        const actuallyIsSaved = isSaved || justSavedWord;
        if (!actuallyIsSaved) return 'kanji-clickable-word';

        let className = 'kanji-clickable-word saved-word';

        // Add grade-specific class
        className += ` grade-${wordGrade}`;

        // If word is marked as known, don't apply saved-word styling
        if (wordKnown) {
            className = 'kanji-clickable-word';
        }

        return className;
    };

    // Render kanji info with grading UI
    const renderKanjiInfo = (kanjiItem) => (
        <div className="kanji-info" key={kanjiItem.literal}>
            <div className="kanji-info-header">
                <span className="kanji-info-text">{kanjiItem.literal}</span>
            </div>

            {kanjiItem.definition ? (
                <div>
                    <p>
                        <span className="kanji-info-header">Meanings:</span>
                        <span className="kanji-info-text">{kanjiItem.definition.meanings.join(', ')}</span>
                    </p>
                    <p>
                        <span className="kanji-info-header">Onyomi:</span>
                        <span className="kanji-info-text">{kanjiItem.definition.onReadings.join(', ')}</span>
                    </p>
                    <p>
                        <span className="kanji-info-header">Kunyomi:</span>
                        <span className="kanji-info-text">{kanjiItem.definition.kunReadings.join(', ')}</span>
                    </p>
                    <p>
                        <span className="kanji-info-header">Grade:</span>
                        <span className="kanji-info-text">{kanjiItem.definition.grade}</span>
                    </p>
                    <p>
                        <span className="kanji-info-header">Frequency:</span>
                        <span className="kanji-info-text">{kanjiItem.definition.frequency}</span>
                    </p>
                </div>
            ) : (
                <p>No definition found for this kanji.</p>
            )}

            <hr className="definition-separator" />

            <div className="stories-WordLookup-popup-buttons" onClick={(e) => e.stopPropagation()}>
                {!(isKanjiSaved(kanjiItem.literal) || justSavedKanji === kanjiItem.literal) ? (
                    <button className="save-word-btn" onClick={(e) => {
                        e.stopPropagation();
                        handleSaveKanji(kanjiItem.literal);
                    }}>
                        Save Kanji
                    </button>
                ) : (
                    <button className="delete-word-btn" onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteKanji(kanjiItem.literal);
                    }}>
                        Delete Kanji
                    </button>
                )}
            </div>

            {renderKanjiGradingUI(kanjiItem.literal)}
        </div>
    );

    const handlePlayWord = async (wordToPlay) => {
        if (isPlaying) return;
        const wordForTTS = wordToPlay || word;
        
        // Check cache first
        if (cachedAudioUrl.current) {
            try {
                setIsPlaying(true);
                const audio = new Audio(cachedAudioUrl.current);
                audio.crossOrigin = 'anonymous';
                audio.onended = () => setIsPlaying(false);
                audio.onerror = () => {
                    cachedAudioUrl.current = null;
                    setIsPlaying(false);
                };
                await audio.play();
                return;
            } catch (e) {
                cachedAudioUrl.current = null;
                setIsPlaying(false);
            }
        }

        try {
            setIsPlaying(true);
            const payload = {
                text: wordForTTS,
                learningLanguage: 'Japanese',
            };
            const data = await apiClient.post('/api/tts', payload);
            const url = data.audio;
            cachedAudioUrl.current = url;
            const audio = new Audio(url);
            audio.crossOrigin = 'anonymous';
            audio.onended = () => setIsPlaying(false);
            audio.onerror = () => {
                cachedAudioUrl.current = null;
                setIsPlaying(false);
            };
            await audio.play();
        } catch (error) {
            logger.error('KanjiLookup - Error playing TTS:', error);
            cachedAudioUrl.current = null;
            setIsPlaying(false);
        }
    };

    const renderPopupContent = () => (
        <div onClick={(e) => e.stopPropagation()}>
            <div className="kanji-definition-container">
                <div className="stories-word-heading-row">
                    <strong>{word}</strong>
                    <button
                        className="stories-speaker-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePlayWord(word);
                        }}
                        disabled={isPlaying}
                        title="Play pronunciation"
                    >
                        <SpeakerIcon className="stories-speaker-icon" />
                    </button>
                </div>
                <hr className="definition-separator" />
                <div className="stories-definition-content" onClick={(e) => e.stopPropagation()}>
                    <p>{primaryDefinition}</p>
                </div>

                {/* Context accordion section */}
                {contextData && contextData.context_note && (
                    <div className="JP-stories-definition-accordion">
                        <div
                            className="JP-stories-definition-accordion-header"
                            onClick={(e) => toggleAccordion('context', e)}
                        >
                            <h4 className="JP-stories-definition-accordion-title">Context</h4>
                            <span className="JP-stories-definition-accordion-icon">
                                {openAccordion === 'context' ? '▼' : '▶'}
                            </span>
                        </div>
                        {openAccordion === 'context' && (
                            <div className="JP-stories-definition-accordion-content">
                                <p className="context-note">{contextData.context_note}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Grammar accordion section */}
                {contextData && contextData.grammar_note && (
                    <div className="JP-stories-definition-accordion">
                        <div
                            className="JP-stories-definition-accordion-header"
                            onClick={(e) => toggleAccordion('grammar', e)}
                        >
                            <h4 className="JP-stories-definition-accordion-title">Grammar</h4>
                            <span className="JP-stories-definition-accordion-icon">
                                {openAccordion === 'grammar' ? '▼' : '▶'}
                            </span>
                        </div>
                        {openAccordion === 'grammar' && (
                            <div className="JP-stories-definition-accordion-content">
                                <p className="grammar-note">{contextData.grammar_note}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Example Sentences accordion section */}
                {contextData && contextData.examples && Array.isArray(contextData.examples) && contextData.examples.length > 0 && (
                    <div className="JP-stories-definition-accordion">
                        <div
                            className="JP-stories-definition-accordion-header"
                            onClick={(e) => toggleAccordion('examples', e)}
                        >
                            <h4 className="JP-stories-definition-accordion-title">Example Sentences</h4>
                            <span className="JP-stories-definition-accordion-icon">
                                {openAccordion === 'examples' ? '▼' : '▶'}
                            </span>
                        </div>
                        {openAccordion === 'examples' && (
                            <div className="JP-stories-definition-accordion-content">
                                {contextData.examples.map((example, index) => (
                                    <div key={index} style={{ marginBottom: '12px' }}>
                                        <p style={{
                                            color: '#ffffffff',
                                            fontWeight: '500',
                                            marginBottom: '4px',
                                            fontSize: '15px'
                                        }}>
                                            {example.sentence}
                                        </p>
                                        <p style={{
                                            color: '#ddddddff',
                                            fontStyle: 'italic',
                                            marginTop: '0',
                                            fontSize: '14px'
                                        }}>
                                            {example.translation}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Loading indicator */}
                {loading && <p>Loading...</p>}

                <div className="stories-WordLookup-popup-buttons" onClick={(e) => e.stopPropagation()}>
                    {!(isSaved || justSavedWord) ? (
                        <button className="save-word-btn" onClick={(e) => {
                            e.stopPropagation();
                            handleSaveWord(e);
                        }}>Save Word</button>
                    ) : (
                        <button className="delete-word-btn" onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteWord(e);
                        }}>Delete Word</button>
                    )}
                </div>

                {/* Word grading UI */}
                {renderWordGradingUI()}

                {error && <div className="stories-error-text">{error}</div>}
            </div>

            <hr className="definition-separator" />
            <div className="kanji-definition-container">
                <h4 className="kanji-components-title">Kanji Components:</h4>
                {kanjiDetails.map((kanjiItem, index) => (
                    <div key={index} className="stories-kanji-accordion">
                        <div
                            className="stories-kanji-accordion-header"
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpenAccordionIndex(index === openAccordionIndex ? null : index);
                            }}
                        >
                            <span className="kanji-accordion-header-char">
                                {kanjiItem.literal}
                            </span>
                            <span className="stories-kanji-accordion-icon">
                                {openAccordionIndex === index ? '▼' : '▶'}
                            </span>
                        </div>
                        {openAccordionIndex === index && (
                            <div className="stories-kanji-accordion-body" onClick={(e) => e.stopPropagation()}>
                                {renderKanjiInfo(kanjiItem)}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <span
            ref={spanRef}
            className={getWordClass()}
            onClick={handleClick}
        >
            {children ?
                // When children are provided, we need to check if they contain kanji and apply highlighting
                React.Children.map(children, child => {
                    if (typeof child === 'string') {
                        // Process each character in the string
                        return Array.from(child).map((char, idx) => {
                            // Check if this character is a kanji that should be highlighted
                            const isKanjiChar = /[\u4E00-\u9FFF]/.test(char);
                            const kanjiClass = isKanjiChar ? getKanjiClass(char) : '';

                            // Apply the kanji class to highlight saved kanji in the text
                            return (
                                <span
                                    key={idx}
                                    className={kanjiClass}
                                >
                                    {char}
                                </span>
                            );
                        });
                    }
                    return child;
                })
                : word // If no children, render the word directly
            }
            {showPopup && ReactDOM.createPortal(
                <div
                    ref={popupRef}
                    className="stories-definition-popup"
                    style={{
                        position: 'absolute',
                        top: popupPosition.top,
                        left: popupPosition.left,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {renderPopupContent()}
                </div>,
                document.body
            )}
        </span>
    );
};

export default KanjiLookup;